import { useFetcher } from "~/hooks/useFetcher";
import { ConversationProvider } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { ArtifactCardActionsProvider } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactCardActions";
import { ArtifactPreviewBody } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactPreviewBody";
import type { ArtifactPreview } from "~/services/artifacts";
import { PinnedArtifact, pinRef, pinnedArtifactPreviewPath } from "~/services/pinnedArtifacts";

interface PinnedArtifactCardProps {
  pin: PinnedArtifact;
  canCurate: boolean;
  onUnpin: () => void;
  isLineageOpen: boolean;
  onOpenLineage: () => void;
}

// One shared artifact on the project artifacts page: the same card a session
// shows, titled with the pin's name, whose Lineage opens the page's panel.
//
// The preview resolves through the *pin*, not through an artifact id, which is
// what lets a member render a card someone else shared and what makes the card
// follow a refresh on its own — the pin moves, the next read renders the new
// artifact, and nothing here has to be invalidated.
export const PinnedArtifactCard = ({
  pin,
  canCurate,
  onUnpin,
  isLineageOpen,
  onOpenLineage,
}: PinnedArtifactCardProps) => {
  const { data, error, isLoading } = useFetcher<ArtifactPreview>(
    pinnedArtifactPreviewPath(pin.project_id, pin.id)
  );
  const resolve = () => ({
    title: pin.display_name,
    // Open to every member: refreshing is member-gated, and how the artifact
    // was made is what sharing it claims.
    lineage: { isOpen: isLineageOpen, open: onOpenLineage },
    share: canCurate ? { isShared: true, isReady: true, toggle: onUnpin } : null,
  });

  return (
    // No conversation is in scope here: the artifact belongs to whoever shared
    // or last refreshed it. A null id keeps the renderers from offering
    // conversation-scoped actions this reader cannot perform; the pin in scope
    // is what they download through instead.
    <ConversationProvider conversationId={null} pin={pinRef(pin)}>
      <ArtifactCardActionsProvider resolve={resolve}>
        <ArtifactPreviewBody
          preview={data}
          name={pin.display_name}
          error={error}
          isLoading={isLoading}
        />
      </ArtifactCardActionsProvider>
    </ConversationProvider>
  );
};
